import { useEffect, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  X,
  Filter,
  Image,
  Video,
  Camera,
  Upload,
  Sparkles,
  FileSearch,
  Check,
  Loader2,
} from 'lucide-react'
import { api } from '@/lib/api'
import { getAssetAspectRatioStyle, getAssetThumbnailUrl } from '@/lib/assets'
import { getAssetUploadAccept, validateAssetFiles } from '@/lib/assetUpload'
import { Button } from '@/components/ui/button'
import { toast } from '@/components/ui/toaster'
import type { Asset, SourceType } from '@/types'

const SOURCE_TYPE_CONFIG: Record<SourceType, { label: string; icon: typeof Camera }> = {
  screenshot: { label: 'Screenshot', icon: Camera },
  uploaded: { label: 'Uploaded', icon: Upload },
  generated: { label: 'Generated', icon: Sparkles },
  reference: { label: 'Reference', icon: FileSearch },
  feedback_reference: { label: 'FB Ref', icon: FileSearch },
  feedback_variant: { label: 'FB Variant', icon: FileSearch },
}

interface AssetPickerModalProps {
  open: boolean
  onClose: () => void
  onSelect: (asset: Asset) => void
  onSelectMany?: (assets: Asset[]) => void
  title?: string
  filterProductId?: string
  assetType?: 'image' | 'video'
  requirementId?: string
}

export function AssetPickerModal({
  open,
  onClose,
  onSelect,
  onSelectMany,
  title = 'Select Image',
  filterProductId,
  assetType = 'image',
  requirementId,
}: AssetPickerModalProps) {
  const queryClient = useQueryClient()
  const [productId, setProductId] = useState<string>(filterProductId || '')
  const [sourceType, setSourceType] = useState<SourceType | ''>('')
  const [offset, setOffset] = useState(0)
  const [isUploading, setIsUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [uploadingFileIndex, setUploadingFileIndex] = useState(0)
  const [uploadingFileCount, setUploadingFileCount] = useState(0)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const limit = 12

  useEffect(() => {
    if (!open) return
    setProductId(filterProductId || '')
    setSourceType('')
    setOffset(0)
  }, [open, filterProductId, assetType])

  // Fetch products for filter dropdown
  const { data: products } = useQuery({
    queryKey: ['products'],
    queryFn: () => api.getProducts(0, 100),
    enabled: open,
  })

  // Fetch assets with filters
  const { data: assets, isLoading } = useQuery({
    queryKey: ['assets-picker', productId, sourceType, assetType, offset, requirementId],
    queryFn: () =>
      api.getAssets({
        product_id: productId || undefined,
        source_type: sourceType || undefined,
        asset_type: assetType,
        offset,
        limit,
        include_original_urls: false,
        requirement_id: requirementId,
      }),
    enabled: open,
  })

  const resolveAsset = async (asset: Asset): Promise<Asset> => {
    return asset.url ? asset : await api.getAsset(asset.id)
  }

  const applySelection = (resolvedAssets: Asset[]) => {
    if (resolvedAssets.length === 0) {
      return
    }
    if (onSelectMany) {
      onSelectMany(resolvedAssets)
      return
    }
    resolvedAssets.forEach((asset) => onSelect(asset))
  }

  const handleSelect = (asset: Asset) => {
    void (async () => {
      try {
        const resolvedAsset = await resolveAsset(asset)
        onSelect(resolvedAsset)
        onClose()
      } catch {
        toast({
          title: 'Unable to load asset details',
          description: 'Please try again.',
          variant: 'destructive',
        })
      }
    })()
  }

  const handleUploadClick = () => {
    if (!isUploading) {
      fileInputRef.current?.click()
    }
  }

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const validFiles = validateAssetFiles(Array.from(e.target.files || []), assetType)
    e.target.value = ''
    if (validFiles.length === 0) return

    setIsUploading(true)
    setUploadProgress(0)
    setUploadingFileIndex(0)
    setUploadingFileCount(validFiles.length)

    try {
      const uploadedAssets: Asset[] = []
      const failedFiles: string[] = []

      for (const [index, file] of validFiles.entries()) {
        setUploadingFileIndex(index + 1)
        try {
          const uploadedAsset = await api.uploadAssetWithProgress(
            file,
            productId || undefined,
            undefined,
            (progress) => {
              const overallProgress = Math.round(((index + progress / 100) / validFiles.length) * 100)
              setUploadProgress(overallProgress)
            },
            requirementId,
          )
          uploadedAssets.push(await resolveAsset(uploadedAsset))
        } catch {
          failedFiles.push(file.name)
        }
      }

      if (uploadedAssets.length > 0) {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ['assets-picker'] }),
          queryClient.invalidateQueries({ queryKey: ['assets'] }),
          queryClient.invalidateQueries({ queryKey: ['assets-stats'] }),
        ])

        applySelection(uploadedAssets)
        onClose()
      }

      if (uploadedAssets.length > 0 && failedFiles.length === 0) {
        toast({
          title:
            uploadedAssets.length === 1
              ? `${assetType === 'image' ? 'Image' : 'Video'} uploaded to library`
              : `${uploadedAssets.length} ${assetType === 'image' ? 'images' : 'videos'} uploaded to library`,
          description:
            uploadedAssets.length === 1
              ? `The new reference ${assetType} was saved and selected.`
              : `The new reference ${assetType === 'image' ? 'images' : 'videos'} were saved and selected.`,
        })
      } else if (uploadedAssets.length > 0) {
        toast({
          title: `Uploaded ${uploadedAssets.length} of ${validFiles.length} ${assetType === 'image' ? 'images' : 'videos'}`,
          description: 'Successful uploads were added as references. Some files failed to upload.',
        })
      } else {
        toast({
          title: 'Upload failed',
          description: 'None of the selected files could be uploaded.',
          variant: 'destructive',
        })
      }
    } catch {
      toast({
        title: 'Upload failed',
        description: 'Please try again.',
        variant: 'destructive',
      })
    } finally {
      setIsUploading(false)
      setUploadProgress(0)
      setUploadingFileIndex(0)
      setUploadingFileCount(0)
    }
  }

  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl w-full max-w-4xl max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={getAssetUploadAccept(assetType)}
          multiple
          className="hidden"
          onChange={handleFileChange}
          disabled={isUploading}
        />

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-lg font-semibold">{title}</h2>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleUploadClick}
              disabled={isUploading}
            >
              {isUploading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Upload className="h-4 w-4 mr-2" />
              )}
              {isUploading
                ? uploadingFileCount > 1
                  ? `Uploading ${uploadingFileIndex}/${uploadingFileCount} (${uploadProgress}%)`
                  : `Uploading ${uploadProgress}%`
                : `Upload ${assetType === 'image' ? 'images' : 'videos'}`}
            </Button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="px-6 py-3 border-b bg-gray-50">
          <div className="flex flex-wrap items-center gap-3">
            {/* Product Filter */}
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-gray-400" />
              <select
                value={productId}
                onChange={(e) => {
                  setProductId(e.target.value)
                  setOffset(0)
                }}
                className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary"
              >
                <option value="">All Products</option>
                {products?.items.map((product) => (
                  <option key={product.id} value={product.id}>
                    {product.product_name}
                  </option>
                ))}
              </select>
            </div>

            {/* Source Type Filter */}
            <div className="flex items-center gap-1">
              <button
                onClick={() => {
                  setSourceType('')
                  setOffset(0)
                }}
                className={`px-2.5 py-1.5 text-xs rounded-lg transition-colors ${
                  sourceType === '' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                All
              </button>
              {(Object.keys(SOURCE_TYPE_CONFIG) as SourceType[]).map((type) => {
                const config = SOURCE_TYPE_CONFIG[type]
                const Icon = config.icon
                return (
                  <button
                    key={type}
                    onClick={() => {
                      setSourceType(type)
                      setOffset(0)
                    }}
                    className={`px-2.5 py-1.5 text-xs rounded-lg transition-colors flex items-center gap-1 ${
                      sourceType === type
                        ? 'bg-gray-900 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    <Icon className="h-3 w-3" />
                    {config.label}
                  </button>
                )
              })}
            </div>
          </div>
        </div>

        {/* Asset Grid */}
        <div className="flex-1 overflow-y-auto p-6">
          {isLoading ? (
            <div className="flex items-center justify-center h-48 text-gray-500">
              Loading assets...
            </div>
          ) : assets?.items.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-48 text-gray-400">
              <Image className="h-12 w-12 mb-3" />
              <p>{`No ${assetType}s found`}</p>
              <p className="text-sm mt-1">
                {`Try adjusting your filters or upload a new ${assetType}`}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-3 md:grid-cols-4 gap-4">
              {assets?.items.map((asset) => (
                <AssetPickerItem
                  key={asset.id}
                  asset={asset}
                  onSelect={() => handleSelect(asset)}
                />
              ))}
            </div>
          )}
        </div>

        {/* Pagination */}
        {assets && assets.total > limit && (
          <div className="px-6 py-3 border-t flex items-center justify-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - limit))}
            >
              Previous
            </Button>
            <span className="text-sm text-gray-500">
              Page {Math.floor(offset / limit) + 1} of {Math.ceil(assets.total / limit)}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={offset + limit >= assets.total}
              onClick={() => setOffset(offset + limit)}
            >
              Next
            </Button>
          </div>
        )}

        {/* Footer */}
        <div className="px-6 py-4 border-t bg-gray-50">
          <Button variant="outline" onClick={onClose} className="w-full">
            Cancel
          </Button>
        </div>
      </div>
    </div>
  )
}

function AssetPickerItem({
  asset,
  onSelect,
}: {
  asset: Asset
  onSelect: () => void
}) {
  const [isHovered, setIsHovered] = useState(false)
  const config = SOURCE_TYPE_CONFIG[asset.source_type]
  const Icon = config.icon
  const thumbnailUrl = getAssetThumbnailUrl(asset)
  const aspectStyle = getAssetAspectRatioStyle(asset)
  const frameClassName = `bg-gray-100 rounded-lg overflow-hidden ring-2 ring-transparent hover:ring-primary transition-all ${
    aspectStyle ? '' : 'aspect-square'
  }`

  return (
    <div
      className="relative cursor-pointer group"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={onSelect}
    >
      {/* Thumbnail */}
      <div className={frameClassName} style={aspectStyle}>
        {thumbnailUrl ? (
          <img
            src={thumbnailUrl}
            alt={asset.description || 'Asset'}
            className="w-full h-full object-contain bg-gray-50"
            loading="lazy"
            decoding="async"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gray-50">
            {asset.asset_type === 'video' ? (
              <div className="flex flex-col items-center gap-1.5 text-gray-400">
                <Video className="h-6 w-6" />
                <span className="text-[10px] font-medium uppercase tracking-wide">No thumbnail</span>
              </div>
            ) : (
              <Image className="h-8 w-8 text-gray-300" />
            )}
          </div>
        )}
      </div>

      {/* Source Type Badge */}
      <div className="absolute top-2 left-2 px-1.5 py-0.5 rounded text-xs font-medium flex items-center gap-0.5 bg-white/90 text-gray-700">
        <Icon className="h-3 w-3" />
      </div>

      {/* Hover overlay */}
      {isHovered && (
        <div className="absolute inset-0 bg-primary/20 rounded-lg flex items-center justify-center">
          <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center">
            <Check className="h-4 w-4 text-white" />
          </div>
        </div>
      )}
    </div>
  )
}
